import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { LocalizedLink } from "@/components/LocalizedLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CheckCircle2, AlertCircle, Loader2, Copy, RefreshCw, ExternalLink, Download, ChevronDown, RotateCcw } from "lucide-react";
import { BATH_CANONICAL } from "@/lib/bathImageMap";
import { toast } from "@/hooks/use-toast";

// Resolved URLs for all main product hero images (ES6 imports → hashed paths)
import luxaImg from "@/assets/luxa-series-product-optimized.webp";
import novaImg from "@/assets/nova-series-product-main.jpg";
import lunaImg from "@/assets/luna-m9-main-optimized.webp";
import lumaImg from "@/assets/luma-series-product.jpg";
import ariaImg from "@/assets/aria-series-product-main-opt.webp";
import aureliusImg from "@/assets/aurelius-series-product-main.jpg";
import aureluxImg from "@/assets/aurelux-series-product-main.jpg";
import venaImg from "@/assets/vena-series-product.jpg";
import k2Img from "@/assets/k2-hero-desert.jpg";
import s1Img from "@/assets/s1-hero-egg.jpg";
import m3Img from "@/assets/m3-hero-main.jpg";
import l1Img from "@/assets/l1-product-main.png";

/**
 * /:lang/image-audit
 *
 * Global image audit dashboard. Lists every product gallery (smart-toilet
 * lineup + bath & sanitary), shows which canonical asset is mapped to which
 * slot, and probes each one to surface broken / missing files.
 *
 * Internal tool — noindex.
 */

type Slot = {
  key: string;
  /** Resolved URL the browser will request (hashed for /src/assets, raw for /public). */
  url: string;
  /** Source identifier shown to operators (filename or canonical path). */
  source: string;
  label: string;
};

type Group = {
  category: "Smart Toilets" | "Bath & Sanitary" | "Lifestyle";
  product: string;
  productLink?: string;
  slots: Slot[];
};

const SMART_TOILET_GROUPS: Group[] = [
  {
    category: "Smart Toilets",
    product: "LUXA · M80 (Flagship)",
    productLink: "/products/luxa",
    slots: [{ key: "luxa-hero", url: luxaImg, source: "luxa-series-product-optimized.webp", label: "Hero" }],
  },
  {
    category: "Smart Toilets",
    product: "NOVA · TEG5",
    productLink: "/products/nova",
    slots: [{ key: "nova-hero", url: novaImg, source: "nova-series-product-main.jpg", label: "Hero" }],
  },
  {
    category: "Smart Toilets",
    product: "LUNA · M9",
    productLink: "/products/luna",
    slots: [{ key: "luna-hero", url: lunaImg, source: "luna-m9-main-optimized.webp", label: "Hero" }],
  },
  {
    category: "Smart Toilets",
    product: "LUMA",
    productLink: "/products/luma",
    slots: [{ key: "luma-hero", url: lumaImg, source: "luma-series-product.jpg", label: "Hero" }],
  },
  {
    category: "Smart Toilets",
    product: "ARIA · H60",
    productLink: "/products/aria",
    slots: [{ key: "aria-hero", url: ariaImg, source: "aria-series-product-main-opt.webp", label: "Hero" }],
  },
  {
    category: "Smart Toilets",
    product: "AURELIUS · M2",
    productLink: "/products/aurelius",
    slots: [{ key: "aurelius-hero", url: aureliusImg, source: "aurelius-series-product-main.jpg", label: "Hero" }],
  },
  {
    category: "Smart Toilets",
    product: "AURELUX",
    productLink: "/products/aurelux",
    slots: [{ key: "aurelux-hero", url: aureluxImg, source: "aurelux-series-product-main.jpg", label: "Hero" }],
  },
  {
    category: "Smart Toilets",
    product: "VENA · M80F",
    productLink: "/products/vena",
    slots: [{ key: "vena-hero", url: venaImg, source: "vena-series-product.jpg", label: "Hero" }],
  },
  {
    category: "Smart Toilets",
    product: "K2",
    productLink: "/products/k2",
    slots: [{ key: "k2-hero", url: k2Img, source: "k2-hero-desert.jpg", label: "Hero" }],
  },
  {
    category: "Smart Toilets",
    product: "S1",
    productLink: "/products/s1",
    slots: [{ key: "s1-hero", url: s1Img, source: "s1-hero-egg.jpg", label: "Hero" }],
  },
  {
    category: "Smart Toilets",
    product: "M3",
    productLink: "/products/m3",
    slots: [{ key: "m3-hero", url: m3Img, source: "m3-hero-main.jpg", label: "Hero" }],
  },
  {
    category: "Smart Toilets",
    product: "L1",
    productLink: "/products/l1",
    slots: [{ key: "l1-hero", url: l1Img, source: "l1-product-main.png", label: "Hero" }],
  },
];

const bathSlot = (key: string, path: string, label: string): Slot => ({
  key,
  url: path,
  source: `public${path}`,
  label,
});

const BATH_GROUPS: Group[] = [
  {
    category: "Bath & Sanitary",
    product: "ARIA Sculpt Bathtub",
    productLink: "/bath-sanitary/aria-sculpt",
    slots: [
      bathSlot("ariaSculptHero", BATH_CANONICAL.ariaSculptHero, "Hero · fluted freestanding"),
      bathSlot("ariaSculptDetail", BATH_CANONICAL.ariaSculptDetail, "Fluted exterior detail"),
      bathSlot("ariaSculptTop", BATH_CANONICAL.ariaSculptTop, "Matte white top-down"),
      bathSlot("ariaSculptNoir", BATH_CANONICAL.ariaSculptNoir, "Matte black noir side"),
      bathSlot("ariaSculptNoirTop", BATH_CANONICAL.ariaSculptNoirTop, "Matte black noir top-down"),
    ],
  },
  {
    category: "Bath & Sanitary",
    product: "ARIA Diamond Bathtub",
    productLink: "/bath-sanitary/aria-diamond",
    slots: [
      bathSlot("ariaDiamondHero", BATH_CANONICAL.ariaDiamondHero, "Hero · diamond freestanding"),
      bathSlot("ariaDiamondDetail", BATH_CANONICAL.ariaDiamondDetail, "Diamond hammered texture"),
      bathSlot("ariaDiamondTop", BATH_CANONICAL.ariaDiamondTop, "Top-down view"),
      bathSlot("ariaDiamondNoir", BATH_CANONICAL.ariaDiamondNoir, "Matte black noir finish"),
      bathSlot("ariaDiamondSuite", BATH_CANONICAL.ariaDiamondSuite, "Hotel spa suite lifestyle"),
    ],
  },
  {
    category: "Bath & Sanitary",
    product: "ARIA Monolith Tub",
    productLink: "/bath-sanitary/aria-monolith",
    slots: [
      bathSlot("ariaMonolith", BATH_CANONICAL.ariaMonolith, "Hero"),
      bathSlot("ariaMonolithDetail", BATH_CANONICAL.ariaMonolithDetail, "Seamless cast rim"),
      bathSlot("ariaMonolithSide", BATH_CANONICAL.ariaMonolithSide, "Side profile"),
      bathSlot("ariaMonolithSuite", BATH_CANONICAL.ariaMonolithSuite, "Hotel suite lifestyle"),
      bathSlot("ariaMonolithVilla", BATH_CANONICAL.ariaMonolithVilla, "Private villa lifestyle"),
    ],
  },
  {
    category: "Bath & Sanitary",
    product: "VENA Counter Basin",
    productLink: "/bath-sanitary/vena-basin",
    slots: [
      bathSlot("venaBasin", BATH_CANONICAL.venaBasin, "Hero"),
      bathSlot("venaBasinDetail", BATH_CANONICAL.venaBasinDetail, "Bowl detail"),
      bathSlot("venaBasinTop", BATH_CANONICAL.venaBasinTop, "Top-down view"),
      bathSlot("venaBasinSuite", BATH_CANONICAL.venaBasinSuite, "Hotel suite vanity"),
      bathSlot("venaBasinResidence", BATH_CANONICAL.venaBasinResidence, "Powder room lifestyle"),
    ],
  },
  {
    category: "Bath & Sanitary",
    product: "VENA Vanity Console",
    productLink: "/bath-sanitary/vena-vanity",
    slots: [
      bathSlot("venaVanity", BATH_CANONICAL.venaVanity, "Hero"),
      bathSlot("venaVanityDetail", BATH_CANONICAL.venaVanityDetail, "Integrated basin detail"),
      bathSlot("venaVanitySide", BATH_CANONICAL.venaVanitySide, "Wall-hung side profile"),
      bathSlot("venaVanitySuite", BATH_CANONICAL.venaVanitySuite, "Hotel suite lifestyle"),
      bathSlot("venaVanityResidence", BATH_CANONICAL.venaVanityResidence, "Residence lifestyle"),
    ],
  },
  {
    category: "Lifestyle",
    product: "Shared Lifestyle Scenes",
    slots: [
      bathSlot("lifestyleSuite", BATH_CANONICAL.lifestyleSuite, "Hotel suite (shared)"),
      bathSlot("lifestyleResidence", BATH_CANONICAL.lifestyleResidence, "Residence (shared)"),
    ],
  },
];

const ALL_GROUPS: Group[] = [...SMART_TOILET_GROUPS, ...BATH_GROUPS];

type Status = "pending" | "ok" | "missing";

async function probe(url: string): Promise<Status> {
  try {
    const res = await fetch(url + (url.includes("?") ? "&" : "?") + "_audit=" + Date.now(), {
      method: "GET",
      cache: "no-store",
    });
    if (!res.ok) return "missing";
    const ct = res.headers.get("content-type") || "";
    if (!ct.startsWith("image/")) return "missing"; // SPA shell fallback
    const len = Number(res.headers.get("content-length") || "0");
    if (len > 0 && len < 200) return "missing";
    return "ok";
  } catch {
    return "missing";
  }
}

type ExportFilter = "all" | "ok" | "missing" | "pending";
const EXPORT_FILTER_STORAGE_KEY = "imageAudit:lastExportFilter";

const ImageAudit = () => {
  const [results, setResults] = useState<Record<string, Status>>({});
  const [scanning, setScanning] = useState(false);
  const [filter, setFilter] = useState("");
  const [lastExportFilter, setLastExportFilter] = useState<ExportFilter>(() => {
    if (typeof window === "undefined") return "all";
    const stored = window.localStorage.getItem(EXPORT_FILTER_STORAGE_KEY);
    return stored === "ok" || stored === "missing" || stored === "pending" || stored === "all"
      ? (stored as ExportFilter)
      : "all";
  });

  const runScan = async () => {
    setScanning(true);
    const init: Record<string, Status> = {};
    ALL_GROUPS.forEach((g) => g.slots.forEach((s) => (init[s.key] = "pending")));
    setResults(init);

    const all = ALL_GROUPS.flatMap((g) => g.slots.map((s) => ({ groupKey: g.product, ...s })));
    for (let i = 0; i < all.length; i += 6) {
      const batch = all.slice(i, i + 6);
      const statuses = await Promise.all(batch.map((s) => probe(s.url)));
      setResults((prev) => {
        const next = { ...prev };
        batch.forEach((s, j) => (next[s.key] = statuses[j]));
        return next;
      });
    }
    setScanning(false);
  };

  useEffect(() => {
    runScan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = useMemo(() => {
    const all = ALL_GROUPS.flatMap((g) => g.slots);
    return all.reduce(
      (acc, s) => {
        const st = results[s.key] || "pending";
        acc[st] = (acc[st] || 0) + 1;
        acc.total += 1;
        return acc;
      },
      { ok: 0, missing: 0, pending: 0, total: 0 } as Record<Status | "total", number>,
    );
  }, [results]);

  const filteredGroups = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return ALL_GROUPS;
    return ALL_GROUPS.map((g) => ({
      ...g,
      slots: g.slots.filter(
        (s) =>
          g.product.toLowerCase().includes(q) ||
          s.label.toLowerCase().includes(q) ||
          s.source.toLowerCase().includes(q),
      ),
    })).filter((g) => g.slots.length > 0);
  }, [filter]);

  const copyMissing = () => {
    const lines = ALL_GROUPS.flatMap((g) =>
      g.slots
        .filter((s) => results[s.key] === "missing")
        .map((s) => `[${g.product}] ${s.label} → ${s.source}`),
    );
    if (!lines.length) {
      toast({ title: "Nothing to copy", description: "All audited images resolved successfully." });
      return;
    }
    navigator.clipboard.writeText(lines.join("\n"));
    toast({ title: "Copied", description: `${lines.length} missing entries copied.` });
  };

  const exportCsv = (statusFilter: ExportFilter = lastExportFilter) => {
    setLastExportFilter(statusFilter);
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(EXPORT_FILTER_STORAGE_KEY, statusFilter);
      } catch {
        /* ignore storage errors (private mode, quota) */
      }
    }
    const escape = (val: string) => {
      const s = String(val ?? "");
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = ["Category", "Product", "Slot", "Mapped Asset", "Status", "Resolved URL"];
    const rows = ALL_GROUPS.flatMap((g) =>
      g.slots
        .filter((s) => statusFilter === "all" || (results[s.key] || "pending") === statusFilter)
        .map((s) => {
          const st = results[s.key] || "pending";
          const absoluteUrl = s.url.startsWith("http")
            ? s.url
            : (typeof window !== "undefined" ? window.location.origin : "") + s.url;
          return [g.category, g.product, s.label, s.source, st, absoluteUrl].map(escape).join(",");
        }),
    );
    if (!rows.length) {
      toast({ title: "Nothing to export", description: `No images match status "${statusFilter}".` });
      return;
    }
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.href = url;
    a.download = `image-audit-${statusFilter}-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: "Exported", description: `${rows.length} ${statusFilter} rows downloaded as CSV.` });
  };

  const renderGroup = (g: Group) => {
    const groupOk = g.slots.filter((s) => results[s.key] === "ok").length;
    const groupMissing = g.slots.filter((s) => results[s.key] === "missing").length;
    return (
      <div key={g.product} className="rounded-sm border border-neutral-800 bg-neutral-950/60">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-800 px-6 py-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400/70">{g.category}</p>
            <h2 className="mt-1 font-serif text-xl text-neutral-100">{g.product}</h2>
            <p className="mt-1 text-xs text-neutral-500">
              {groupOk} / {g.slots.length} resolved
              {groupMissing > 0 && <span className="ml-2 text-amber-300">· {groupMissing} broken</span>}
            </p>
          </div>
          {g.productLink && (
            <LocalizedLink to={g.productLink} className="text-xs text-amber-300 hover:text-amber-200">
              View product page →
            </LocalizedLink>
          )}
        </div>

        <ul className="divide-y divide-neutral-800">
          {g.slots.map((s) => {
            const st = results[s.key] || "pending";
            return (
              <li key={s.key} className="flex flex-wrap items-center gap-4 px-6 py-3">
                <div className="h-12 w-12 shrink-0 overflow-hidden rounded-sm border border-neutral-800 bg-neutral-900">
                  {st === "ok" && (
                    <img src={s.url} alt={s.label} className="h-full w-full object-cover" loading="lazy" />
                  )}
                </div>
                <div className="flex h-6 w-6 items-center justify-center">
                  {st === "ok" && <CheckCircle2 className="h-5 w-5 text-emerald-400" />}
                  {st === "missing" && <AlertCircle className="h-5 w-5 text-amber-400" />}
                  {st === "pending" && <Loader2 className="h-4 w-4 animate-spin text-neutral-500" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-neutral-200">{s.label}</p>
                  <p className="truncate font-mono text-[11px] text-neutral-500" title={s.source}>
                    {s.source}
                  </p>
                </div>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-neutral-500 hover:text-amber-300"
                  title="Open image"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
                <span
                  className={
                    "rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] " +
                    (st === "ok"
                      ? "bg-emerald-500/10 text-emerald-300"
                      : st === "missing"
                        ? "bg-amber-500/10 text-amber-300"
                        : "bg-neutral-800 text-neutral-400")
                  }
                >
                  {st === "ok" ? "OK" : st === "missing" ? "Broken" : "Checking"}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-neutral-100">
      <Helmet>
        <title>Image Audit | Aureum VII Trading</title>
        <meta name="description" content="Internal global image audit: gallery mappings and broken-asset detection." />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <Navigation />

      <div className="border-b border-amber-500/10 bg-black/40 pt-28">
        <div className="container mx-auto px-6 py-4 text-xs text-neutral-500 lg:px-12">
          <span className="text-neutral-300">Internal · Image Audit</span>
        </div>
      </div>

      <section className="border-b border-amber-500/10 py-12">
        <div className="container mx-auto px-6 lg:px-12">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-amber-400/80">Internal · Asset QA</p>
          <h1 className="font-serif text-3xl text-neutral-50 sm:text-4xl">Global Image Audit</h1>
          <p className="mt-4 max-w-2xl text-neutral-400">
            Live probe of every gallery image referenced across the smart-toilet lineup and the
            Bath &amp; Sanitary collection. Each row shows which canonical file is mapped to
            which product slot and flags any broken or missing assets.
          </p>

          <div className="mt-8 grid gap-4 sm:grid-cols-4">
            <div className="rounded-sm border border-neutral-700 bg-neutral-900/40 p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Total slots</p>
              <p className="mt-2 font-serif text-3xl text-neutral-100">{totals.total}</p>
            </div>
            <div className="rounded-sm border border-emerald-500/20 bg-emerald-500/5 p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-300/80">Resolved</p>
              <p className="mt-2 font-serif text-3xl text-emerald-200">{totals.ok}</p>
            </div>
            <div className="rounded-sm border border-amber-500/30 bg-amber-500/5 p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-300/80">Broken</p>
              <p className="mt-2 font-serif text-3xl text-amber-200">{totals.missing}</p>
            </div>
            <div className="rounded-sm border border-neutral-700 bg-neutral-900/40 p-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Pending</p>
              <p className="mt-2 font-serif text-3xl text-neutral-200">{totals.pending}</p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button
              onClick={runScan}
              disabled={scanning}
              className="bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:from-amber-400 hover:to-amber-500"
            >
              {scanning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              {scanning ? "Scanning…" : "Re-scan"}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="border-emerald-500/40 bg-transparent text-emerald-200 hover:bg-emerald-500/10"
                  title={`Will export: ${
                    lastExportFilter === "ok"
                      ? "Resolved only"
                      : lastExportFilter === "missing"
                        ? "Broken only"
                        : lastExportFilter === "pending"
                          ? "Pending only"
                          : "All"
                  }`}
                >
                  <Download className="mr-2 h-4 w-4" /> Export CSV
                  <span className="ml-2 hidden text-[11px] font-normal text-emerald-300/80 sm:inline">
                    ·
                  </span>
                  <span
                    className={`ml-2 rounded-md border px-2 py-0.5 text-[11px] font-semibold ${
                      lastExportFilter === "ok"
                        ? "border-emerald-400/50 bg-emerald-500/15 text-emerald-200"
                        : lastExportFilter === "missing"
                          ? "border-amber-400/50 bg-amber-500/15 text-amber-200"
                          : lastExportFilter === "pending"
                            ? "border-neutral-500/50 bg-neutral-500/15 text-neutral-200"
                            : "border-emerald-400/40 bg-emerald-500/10 text-emerald-100"
                    }`}
                  >
                    {lastExportFilter === "ok"
                      ? `Resolved only (${totals.ok})`
                      : lastExportFilter === "missing"
                        ? `Broken only (${totals.missing})`
                        : lastExportFilter === "pending"
                          ? `Pending only (${totals.pending})`
                          : `All (${totals.total})`}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="border-neutral-700 bg-neutral-900 text-neutral-100">
                <DropdownMenuLabel>Export filter (remembered)</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-neutral-800" />
                {(
                  [
                    { key: "all", label: `All (${totals.total})`, cls: "" },
                    { key: "ok", label: `Resolved only (${totals.ok})`, cls: "text-emerald-300" },
                    { key: "missing", label: `Broken only (${totals.missing})`, cls: "text-amber-300" },
                    { key: "pending", label: `Pending only (${totals.pending})`, cls: "text-neutral-400" },
                  ] as { key: ExportFilter; label: string; cls: string }[]
                ).map((opt) => (
                  <DropdownMenuItem
                    key={opt.key}
                    onClick={() => exportCsv(opt.key)}
                    className={`${opt.cls} focus:bg-neutral-800`}
                  >
                    <span className="flex-1">{opt.label}</span>
                    {lastExportFilter === opt.key && (
                      <CheckCircle2 className="ml-2 h-3.5 w-3.5 text-emerald-400" />
                    )}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator className="bg-neutral-800" />
                <DropdownMenuItem
                  onClick={() => {
                    setLastExportFilter("all");
                    if (typeof window !== "undefined") {
                      try {
                        window.localStorage.removeItem(EXPORT_FILTER_STORAGE_KEY);
                      } catch {
                        /* ignore */
                      }
                    }
                    toast({
                      title: "Filter reset",
                      description: "Remembered export filter cleared. Default is now All.",
                    });
                  }}
                  disabled={lastExportFilter === "all"}
                  className="text-neutral-300 focus:bg-neutral-800"
                >
                  <RotateCcw className="mr-2 h-3.5 w-3.5" />
                  <span className="flex-1">Clear remembered filter</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              onClick={copyMissing}
              variant="outline"
              className="border-amber-500/40 bg-transparent text-amber-200 hover:bg-amber-500/10"
            >
              <Copy className="mr-2 h-4 w-4" /> Copy broken list
            </Button>
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by product, slot, or filename…"
              className="max-w-xs border-neutral-700 bg-neutral-900 text-neutral-100 placeholder:text-neutral-500"
            />
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-6 lg:px-12">
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="bg-neutral-900">
              <TabsTrigger value="all">All ({ALL_GROUPS.length})</TabsTrigger>
              <TabsTrigger value="toilets">Smart Toilets ({SMART_TOILET_GROUPS.length})</TabsTrigger>
              <TabsTrigger value="bath">Bath &amp; Sanitary ({BATH_GROUPS.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="mt-6 space-y-6">
              {filteredGroups.map(renderGroup)}
            </TabsContent>
            <TabsContent value="toilets" className="mt-6 space-y-6">
              {filteredGroups.filter((g) => g.category === "Smart Toilets").map(renderGroup)}
            </TabsContent>
            <TabsContent value="bath" className="mt-6 space-y-6">
              {filteredGroups.filter((g) => g.category !== "Smart Toilets").map(renderGroup)}
            </TabsContent>
          </Tabs>

          <div className="mt-8 rounded-sm border border-dashed border-amber-500/20 bg-neutral-900/40 p-5 text-xs text-neutral-500">
            Probes run in your browser, reflecting what real visitors see. For a CI-time check
            covering canonical bath assets and the image sitemap, run{" "}
            <code className="text-amber-300">node scripts/check-image-health.mjs</code>.
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ImageAudit;
